import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import {
  FetchStatus,
  EventType,
  ErrorCode,
  QueryParam,
} from "../commons/Constants";
import { useLanderConfig } from "../commons/ConfigContext";
import { isRollerAdsKeywordClickGeo } from "../commons/HelperFunctions";
import { fetchRollerAds, getRollerAdsPid } from "./rollerAdsFeed";
import { sendRollerAdsEvent } from "./rollerAdsEvent";
import { trackImpression, trackClick } from "../commons/signals";
import Logger from "../commons/Logger";
import { RollerAdsKeywordLinks } from "./RollerAdsKeywordLinks";
import { isValidRedirectUrl } from "./rollerAdsUtils";
import { useYahooRSKeywords } from "../yahoo/useYahooRSKeywords";
import { llmKeywordTerms } from "../commons/llmKeywords";
import { LanderConfig } from "../types/LanderConfig";

/**
 * Which keyword list the 1-click flow shows. First non-empty wins:
 *   1. rollerAds.keywords
 *   2. the API's LLM keywords (PARKING-5307; empty unless the switch is on),
 *      skipped for a ?term= override, which the merged keywords carry, as
 *      Yahoo Partner Supplied Terms do
 *   3. the merged keywords
 * `llmKeywordsUsed` is true only when step 2 supplied the list.
 */
function getKeywordClickState(
  landerConfig: Pick<LanderConfig, "rollerAds" | "keywords">,
  mergedKeywords: string[],
  isKeywordClickGeo: boolean,
  hasTermOverride: boolean
): {
  isKeywordClickFlow: boolean;
  keywords: string[];
  llmKeywordsUsed: boolean;
} {
  if (!isKeywordClickGeo) {
    return { isKeywordClickFlow: false, keywords: [], llmKeywordsUsed: false };
  }

  const rollerAdsKeywords = landerConfig.rollerAds?.keywords;
  if (rollerAdsKeywords && rollerAdsKeywords.length > 0) {
    return {
      isKeywordClickFlow: true,
      keywords: rollerAdsKeywords,
      llmKeywordsUsed: false,
    };
  }

  const llmKeywords = hasTermOverride ? [] : llmKeywordTerms(landerConfig);
  if (llmKeywords.length > 0) {
    return {
      isKeywordClickFlow: true,
      keywords: llmKeywords,
      llmKeywordsUsed: true,
    };
  }

  return {
    isKeywordClickFlow: mergedKeywords.length > 0,
    keywords: mergedKeywords,
    llmKeywordsUsed: false,
  };
}

interface RollerAdsContentProps {
  experimentKeywordClick?: boolean;
  yahooUnapprovedKeywordClick?: boolean;
  /** Send keyword clicks to the second 1-click account (pid 387). */
  keywordClickAccount387?: boolean;
}

export const RollerAdsContent: React.FC<RollerAdsContentProps> = ({
  experimentKeywordClick = false,
  yahooUnapprovedKeywordClick = false,
  keywordClickAccount387 = false,
}) => {
  const { landerConfig, mergedConfig, queryConfig } = useLanderConfig();
  const [fetchState, setFetchState] = useState<number>(FetchStatus.PENDING);

  const { fetchState: yahooRSFetchState, keywords: yahooRSKeywords } =
    useYahooRSKeywords(landerConfig, experimentKeywordClick);

  const effectiveKeywords = useMemo(
    () =>
      mergedConfig?.keywords?.length
        ? mergedConfig.keywords
        : landerConfig.adSense?.keywords ?? [],
    [mergedConfig?.keywords, landerConfig.adSense?.keywords]
  );

  const experimentKeywords =
    experimentKeywordClick && yahooRSFetchState === FetchStatus.SUCCESS
      ? yahooRSKeywords
      : effectiveKeywords;

  // Traffic routed here because Yahoo cannot monetize the market (PARKING-5273)
  // takes the same 1-click path as the keyword-click geos: config keywords with
  // the domain keywords as fallback, and no Yahoo RS fetch. With no keywords at
  // all it degrades to the 0-click redirect, exactly as those geos do today.
  // The 5254 experiment keeps its Yahoo RS terms and never sees the LLM terms:
  // it is handed an empty config for the two lookups in getKeywordClickState.
  // llmKeywordsUsed (PARKING-5307) marks the keyword-click events below: true
  // when the list the visitor clicked from came from the LLM step. VISIT and the
  // 0-click events say nothing about it, so they carry no such field.
  const {
    isKeywordClickFlow,
    keywords: keywordClickKeywords,
    llmKeywordsUsed,
  } = getKeywordClickState(
    experimentKeywordClick ? { rollerAds: {} } : landerConfig,
    experimentKeywords,
    isRollerAdsKeywordClickGeo() ||
      experimentKeywordClick ||
      yahooUnapprovedKeywordClick,
    !!queryConfig[QueryParam.TERM]
  );

  // Fire VISIT event on mount
  useEffect(() => {
    sendRollerAdsEvent(EventType.VISIT, landerConfig);
    trackImpression({ section: "page", widget: "lander_page" });
  }, [landerConfig]);

  // 0-click auto-redirect (skipped for keyword click flow)
  useEffect(() => {
    if (isKeywordClickFlow) return;
    if (experimentKeywordClick && yahooRSFetchState === FetchStatus.PENDING)
      return;

    let ignore = false;
    const pid = getRollerAdsPid({});

    fetchRollerAds({
      rootDomain: landerConfig.domain.rootDomain,
      customerId: landerConfig.customerId,
      keywords: effectiveKeywords.length ? effectiveKeywords : void 0,
    }).then((result) => {
      if (ignore) return;

      if (result.status === 200 && result.ads.length > 0) {
        const firstAd = result.ads[0];

        if (!isValidRedirectUrl(firstAd.url)) {
          setFetchState(FetchStatus.FAILURE);
          sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          pid,
            errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
          });
          Logger.error("RollerAds invalid redirect URL:", firstAd.url);
          return;
        }

        setFetchState(FetchStatus.SUCCESS);

        trackClick({ section: "ads", widget: "ad_redirect" });
        sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
          pid,
          bid: firstAd.bid,
          campaignId: firstAd.campaign_id,
          adUrl: firstAd.url,
        });

        window.location.href = firstAd.url;
      } else if (result.status === 0) {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          pid,
          errorCode: ErrorCode.ERROR_ROLLERADS_BLOCKED,
        });
        Logger.error("RollerAds blocked or missing client IP:", result.error);
      } else if (result.status === 204 || result.ads.length === 0) {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          pid,
          errorCode: ErrorCode.ERROR_ROLLERADS_NO_ADS,
        });
      } else {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
          pid,
          errorCode: ErrorCode.ERROR_ROLLERADS_FETCH,
        });
        Logger.error("RollerAds error:", result.error);
      }
    });

    return () => {
      ignore = true;
    };
  }, [
    landerConfig,
    effectiveKeywords,
    isKeywordClickFlow,
    experimentKeywordClick,
    yahooRSFetchState,
  ]);

  const handleKeywordClick = useCallback(
    async (keyword: string) => {
      setFetchState(FetchStatus.PENDING);
      const pid = getRollerAdsPid({
        keywordClick: true,
        keywordClickAccount387,
      });

      sendRollerAdsEvent(EventType.KEYWORD_CLICK, landerConfig, {
        pid,
        keyword,
        llmKeywordsUsed,
      });

      const result = await fetchRollerAds({
        rootDomain: landerConfig.domain.rootDomain,
        customerId: landerConfig.customerId,
        keywords: [keyword],
        keywordClick: true,
        ...(keywordClickAccount387 && { keywordClickAccount387 }),
      });

      if (result.status === 200 && result.ads.length > 0) {
        const firstAd = result.ads[0];

        if (!isValidRedirectUrl(firstAd.url)) {
          setFetchState(FetchStatus.FAILURE);
          sendRollerAdsEvent(EventType.ERROR, landerConfig, {
        pid,
            errorCode: ErrorCode.ERROR_ROLLERADS_INVALID_URL,
            llmKeywordsUsed,
          });
          return;
        }

        trackClick({ section: "ads", widget: "ad_redirect" });
        sendRollerAdsEvent(EventType.AD_REDIRECT, landerConfig, {
        pid,
          bid: firstAd.bid,
          campaignId: firstAd.campaign_id,
          adUrl: firstAd.url,
          keyword,
          llmKeywordsUsed,
        });

        window.location.href = firstAd.url;
      } else {
        setFetchState(FetchStatus.FAILURE);
        sendRollerAdsEvent(EventType.ERROR, landerConfig, {
        pid,
          errorCode:
            result.status === 0
              ? ErrorCode.ERROR_ROLLERADS_BLOCKED
              : ErrorCode.ERROR_ROLLERADS_NO_ADS,
          llmKeywordsUsed,
        });
      }
    },
    [landerConfig, llmKeywordsUsed, keywordClickAccount387]
  );

  // Wait for Yahoo RS keywords before rendering experiment keyword-click flow
  if (experimentKeywordClick && yahooRSFetchState === FetchStatus.PENDING) {
    return null;
  }

  // 1-click keyword links mode
  if (isKeywordClickFlow) {
    return (
      <RollerAdsKeywordLinks
        keywords={ keywordClickKeywords }
        fetchState={ fetchState }
        onKeywordClick={ handleKeywordClick }
      />
    );
  }

  // 0-click mode rendering
  if (fetchState === FetchStatus.PENDING) {
    return null;
  }

  if (fetchState === FetchStatus.FAILURE) {
    return (
      <div>
        <FormattedMessage id="contentBlockedText" />
      </div>
    );
  }

  return (
    <div>
      <p>
        <FormattedMessage
          id="redirectingText"
          defaultMessage="Redirecting..."
        />
      </p>
    </div>
  );
};
